import type { Category, FormStructure, QuestionItem } from "../../types/form";

/** Path from tree root to the node with `id` (inclusive). */
export function findCategoryPath(
  tree: Category[],
  id: number,
  prefix: Category[] = []
): Category[] | null {
  for (const node of tree) {
    const path = [...prefix, node];
    if (node.id === id) return path;
    if (node.subcategories?.length) {
      const hit = findCategoryPath(node.subcategories, id, path);
      if (hit) return hit;
    }
  }
  return null;
}

/** Leftmost leaf under this node (what the form shows when a section is selected). */
export function resolveLeafCategory(node: Category): Category {
  if (node.subcategories?.length) {
    return resolveLeafCategory(node.subcategories[0]);
  }
  return node;
}

export function collectAllQuestions(cat: Category): QuestionItem[] {
  const out: QuestionItem[] = [...(cat.questions ?? [])];
  for (const sub of cat.subcategories ?? []) {
    out.push(...collectAllQuestions(sub));
  }
  return out;
}

/** Every category id in this subtree (including `cat` itself). Used for accordion expand/collapse. */
export function collectDescendantCategoryIds(cat: Category): number[] {
  const ids: number[] = [];
  const walk = (c: Category) => {
    ids.push(c.id);
    for (const sub of c.subcategories ?? []) walk(sub);
  };
  walk(cat);
  return ids;
}

export function findQuestionAnywhereInStructure(
  structure: FormStructure | null,
  questionId: number
): { question: QuestionItem; category: Category } | null {
  if (!structure) return null;
  const roots =
    structure.categoryTree?.length > 0
      ? structure.categoryTree
      : structure.categories ?? [];

  const visit = (cat: Category): { question: QuestionItem; category: Category } | null => {
    for (const q of cat.questions ?? []) {
      if (q.id === questionId) return { question: q, category: cat };
    }
    for (const sub of cat.subcategories ?? []) {
      const hit = visit(sub);
      if (hit) return hit;
    }
    return null;
  };

  for (const root of roots) {
    const hit = visit(root);
    if (hit) return hit;
  }
  for (const q of structure.uncategorizedQuestions ?? []) {
    if (q.id === questionId) {
      return {
        question: q,
        category: {
          id: -1,
          name: "(Uncategorized)",
          questions: [q],
        },
      };
    }
  }
  return null;
}
